import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { CurriculumDTO } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { useNavigate } from 'react-router-dom';
import { Search, BookMarked, Bot, Play } from 'lucide-react';
import { useMe } from '@/hooks/useMe';

export default function TrainingHome() {
  const nav = useNavigate();
  const { data: me } = useMe();
  const { data: curriculum, isLoading } = useQuery<CurriculumDTO>({
    queryKey: ['curriculum'],
    queryFn: () => api.get<CurriculumDTO>('/api/training/curriculum'),
  });
  const { data: intro } = useQuery<{ id: string; title: string } | null>({
    queryKey: ['intro'],
    queryFn: () => api.get<{ id: string; title: string } | null>('/api/training/intro'),
  });

  const isIntern = me?.role === 'intern';
  const isStaff = me?.role === 'admin' || me?.role === 'consultant';

  if (isLoading) return <div className="p-6 text-tg-hint">Загрузка…</div>;

  const lessons = curriculum?.lessons ?? [];
  const completed = lessons.filter((l) => l.status === 'completed').length;
  const next = lessons.find((l) => l.status === 'available' || l.status === 'in_progress');
  const failed = lessons.find((l) => l.status === 'failed');
  const pct = lessons.length ? Math.round((completed / lessons.length) * 100) : 0;

  const subtitle = isIntern
    ? (completed >= lessons.length ? `Курс пройден · ${pct}%` : `Урок ${completed + 1}/${lessons.length} · ${pct}%`)
    : isStaff
      ? `${lessons.length} уроков · освежить в любой момент`
      : 'Справочник';

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title="Обучение" subtitle={subtitle} />

      <div className="p-4 space-y-4">
        {intro && (
          <button
            onClick={() => nav(`/training/article/${intro.id}`)}
            className="w-full text-left flex items-center gap-3 p-4 rounded-2xl bg-brand-orange/10 border border-brand-orange/30 hover:bg-brand-orange/15 transition-all"
          >
            <div className="text-2xl">👋</div>
            <div className="flex-1">
              <div className="font-semibold">Урок 0 · Начни отсюда</div>
              <div className="text-xs text-tg-hint">
                Короткий вводный гайд — прочитай перед уроками
              </div>
            </div>
            <div className="text-tg-hint text-xl">›</div>
          </button>
        )}

        {isIntern && lessons.length > 0 && (
          <Card className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-tg-hint">Прогресс</span>
              <span className="font-semibold">{pct}%</span>
            </div>
            <ProgressBar value={pct} />
          </Card>
        )}

        {isStaff && completed > 0 && (
          <Card className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-tg-hint">Пройдено</span>
              <span className="font-semibold">
                {completed} / {lessons.length}
              </span>
            </div>
            <ProgressBar value={pct} />
          </Card>
        )}

        {isIntern && next && (
          <Card className="bg-brand-orange text-white space-y-3">
            <div className="text-sm opacity-90">▶ Продолжить</div>
            <div className="text-xl font-semibold">
              Урок {next.dayNumber}. {next.emoji} {next.title}
            </div>
            {next.description && <p className="text-sm opacity-90">{next.description}</p>}
            <Button
              variant="secondary"
              size="lg"
              className="w-full bg-white text-brand-orange hover:bg-white/90"
              onClick={() => nav(`/training/lesson/${next.id}`)}
            >
              <Play className="w-5 h-5 mr-2" />
              Открыть урок
            </Button>
          </Card>
        )}

        {isIntern && !next && failed && (
          <Card className="bg-red-500/5 border border-red-500/20 space-y-3">
            <div className="text-sm text-red-500">❌ Тест не пройден</div>
            <div className="text-xl font-semibold">
              Урок {failed.dayNumber}. {failed.title}
            </div>
            <p className="text-sm text-tg-hint">Нужно набрать 70%+ — попробуй ещё раз</p>
            <Button
              size="lg"
              className="w-full"
              onClick={() => nav(`/training/lesson/${failed.id}/quiz`)}
            >
              Повторить тест
            </Button>
          </Card>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Button variant="secondary" onClick={() => nav('/training/search')}>
            <Search className="w-4 h-4 mr-2" />
            Поиск
          </Button>
          <Button variant="secondary" onClick={() => nav('/training/bookmarks')}>
            <BookMarked className="w-4 h-4 mr-2" />
            Закладки
          </Button>
          <Button variant="secondary" onClick={() => nav('/ai')}>
            <Bot className="w-4 h-4 mr-2" />
            ИИ
          </Button>
        </div>

        {lessons.length > 0 && (
          <Card className="space-y-2">
            <div className="font-semibold mb-2">
              {isIntern ? 'Все уроки' : isStaff ? 'Все уроки (свободный доступ)' : 'Темы'}
            </div>
            {lessons.map((l) => {
              const isDone = l.status === 'completed';
              const isLocked = l.status === 'locked';
              const isFailed = l.status === 'failed';
              const isCurrent = next?.id === l.id;
              return (
                <button
                  key={l.id}
                  disabled={isLocked}
                  onClick={() => nav(`/training/lesson/${l.id}`)}
                  className={
                    'w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all ' +
                    (isCurrent
                      ? 'bg-brand-orange/10 border border-brand-orange/30'
                      : isFailed
                        ? 'bg-red-500/5 border border-red-500/20 hover:bg-red-500/10'
                        : 'hover:bg-tg-secondaryBg disabled:opacity-40')
                  }
                >
                  <div className="text-2xl">{l.emoji ?? '📖'}</div>
                  <div className="flex-1">
                    <div className="font-medium">
                      Урок {l.dayNumber}. {l.title}
                    </div>
                    {isFailed ? (
                      <div className="text-xs text-red-500">Повтори тест</div>
                    ) : (
                      l.estimatedMin && (
                        <div className="text-xs text-tg-hint">~{l.estimatedMin} мин</div>
                      )
                    )}
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-lg">
                      {isDone
                        ? '✅'
                        : isCurrent && isIntern
                          ? '▶'
                          : isLocked
                            ? '🔒'
                            : isFailed
                              ? '❌'
                              : isStaff
                                ? '📖'
                                : '⬜'}
                    </div>
                    {isDone && l.testScore != null && (
                      <div className="text-xs text-tg-hint">{l.testScore.toFixed(1)}/10</div>
                    )}
                  </div>
                </button>
              );
            })}
          </Card>
        )}
      </div>
    </div>
  );
}
